import React, { useCallback } from 'react';
import styled from 'styled-components/macro';

import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper';
import DataWarning from 'components/base/DataWarning/DataWarning';
import Pagination from 'components/data-visualization/Table/Pagination';
import Table from 'components/data-visualization/Table/Table';
import TableBody from 'components/data-visualization/Table/Body';
import SortingArrowTableHead from 'components/data-visualization/Table/SortingHead';
import DropdownTableHead from 'components/data-visualization/Table/DropdownHead';
import GenericComponent, {
  IToolbarItem,
  ICustomizedModeType,
} from 'components/utility/GenericComponent/GenericComponent';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import Layout from 'components/styled/Layout/Layout';
import BaseNoData from 'components/styled/NoData/NoData';
import TableRowLoader from '../TableRow/TableRow.loader';
import NoResults from '../NoResults/NoResults';
import BrowseControls from '../BrowseControls/BrowseControls';
import BrowseTableRow from '../BrowseTableRow/BrowseTableRow';
import TableRow from '../TableRow/TableRow';

import { EntityTypes } from 'context/entityContext';
import { TopicFilterNames } from 'components/TopicFilters/shared';
import { IInnerEntity } from '../shared/types';
import { IMetric } from 'maps/metrics';
import { ILabelValuePair } from 'types/shared';
import { SalesTypeValues } from 'maps/sales-types';
import {
  VersionsLabel,
  ParentEntityItem,
  ParentEntityItemLink,
  ParentEntityItemLoader,
  ActualDate,
} from '../shared/styles';
import TableControls from 'components/base/TableControls/TableControls';

const withChangeMetric = new Set([
  'performance_this_week_to_date',
  'performance_last_week',
  'displayName',
  'performance_last_week_to_date',
  'performance_last_last_week',
  'performance_rtd',
  'performance_wow_in_progress',
  'performance_wow_last_completed',
]);

const GenericComponentWrapper = styled('div')<any>`
  & > section {
    width: 100%;
    margin: ${({ theme, browseRelated }): string =>
        browseRelated ? '0' : theme.spacing.small}
      0;

    @media screen and (min-width: 40em) {
      margin: ${({ theme, browseRelated, browseOpus }): string =>
          browseRelated
            ? '0'
            : browseOpus
            ? theme.spacing.medium
            : theme.spacing.xlarge}
        auto;
    }
    @media screen and (min-width: 50em) {
      ${({ browseRelated }): string => (browseRelated ? 'width: 90%;' : '')}
    }
    @media screen and (min-width: 60em) {
      width: ${({ browseRelated, isTopLists }): number =>
        browseRelated || isTopLists ? 90 : 78.2}%;
      ${Layout.MaxWidth} {
        width: 100% !important;
      }
    }
    @media screen and (min-width: 80em) {
      ${({ browseRelated, isTopLists }): string =>
        browseRelated || isTopLists ? 'width: 75%;' : ''}
    }
  }
  ${AnimatedLoadingWrapper} {
    padding: 0 ${({ theme }): string => theme.spacing.small};
    @media screen and (min-width: 40em) {
      padding: 0 ${({ theme }): string => theme.spacing.xlarge};
    }
  }
  ${TableBody} {
    padding-right: 0;
  }
  ${TableControls} {
    ${({ isTopLists }): string =>
      isTopLists ? 'padding-left: 0; padding-right: 0;' : ''}
  }
`;

/*
 * Variantion of the browse table that explores and sorts all the entities
 * the entities on Opus. It is used on the homepage.
 *
 * @param  {String} props.entityType type of entity (e.g 'track')
 * @param  {Object} props.entityFetch response from `fetchBrowseData`
 * @param  {Function} props.fetchBrowseData callback from `react-refetch`
 * @param  {Object} props.globalFilters customer and/or country filters
 */

interface IBrowseOpusProps {
  dataCy: string;
  extraToolbarItems?: IToolbarItem[] | undefined;
  genericTitle: string;
  glossarySlug: string;
  tableName: string;

  error: boolean;
  loading: boolean;
  reason: any;
  success: boolean;

  activeItem: IMetric;
  allowedFilters: TopicFilterNames[];
  dropdownItems: (
    | IMetric
    | {
        label: string;
        value: string;
      }
  )[];
  entityCount: number;
  entities: any[];
  entityType: any;
  inputValue?: string;
  isExternalUser: boolean;
  isExternalOneLabelUser: boolean;
  isLargeScreen: boolean;
  offset?: number;
  salesType: ILabelValuePair<SalesTypeValues> & {
    base?: string | undefined;
    split?: string | undefined;
  };
  searchValue?: string;
  showDataWarning: boolean;
  sortMetric: IMetric;
  sortOrder: string;
  tableHeaders: any[];
  entityData?: any;
  parentEntity?: any;
  parentEntityId?: string;
  browseRelated?: boolean;
  browseOpus?: boolean;
  newDesign?: boolean;

  changeSortMetric: (...args: any[]) => any;
  handleSearch?: Function;
  handleToggle: Function;
  saveComponentSettings: (settings: {
    selectedSalesType: string;
    browseEntityType: string;
  }) => void;
  setSortMetric: (...args: any[]) => any;
  setSortOrder: (...args: any[]) => any;
  setOffset: (...args: any[]) => any;
  globalFilters?: any;
  entityIds?: any[];
  isSearchHidden?: boolean;
  isTabControlsHidden?: boolean;
  isTopLists?: boolean;
  actualTWTDate?: string;
  isReleaseChecked?: boolean;
  setReleaseChecked?: Function;
  releaseCompilation?: string;
  customizedMode?: ICustomizedModeType;
  showToolbarDropdown?: boolean;
  showToolbarOptions?: boolean;
}

const BrowseTable: React.FC<IBrowseOpusProps> = (props) => {
  const {
    dataCy,
    extraToolbarItems,
    genericTitle,
    glossarySlug,
    tableName,

    error,
    loading,
    reason,
    success,

    activeItem,
    allowedFilters,
    dropdownItems,
    entityCount,
    entities,
    entityType,
    inputValue = '',
    isExternalUser,
    isExternalOneLabelUser,
    isLargeScreen,
    offset = 10,
    salesType,
    searchValue = '',
    sortMetric,
    sortOrder,
    showDataWarning,
    tableHeaders,
    globalFilters,
    entityData,
    parentEntity,
    parentEntityId,
    browseRelated,
    browseOpus,
    newDesign,

    changeSortMetric,
    handleSearch,
    handleToggle,
    saveComponentSettings,
    setSortMetric,
    setSortOrder,
    setOffset = (): void => {}, // eslint-disable-line @typescript-eslint/no-empty-function
    entityIds,
    isSearchHidden = false,
    isTabControlsHidden = false,
    isTopLists = false,
    actualTWTDate,
    isReleaseChecked = true,
    setReleaseChecked = (): void => {}, // eslint-disable-line @typescript-eslint/no-empty-function
    releaseCompilation,
    customizedMode,
    showToolbarDropdown,
    showToolbarOptions,
  } = props;

  const checkHighlighted = useCallback(
    (entity) => {
      if (!entityIds || entityIds.length === 0 || !parentEntity) return;
      return entity[parentEntity.type].id === entityIds[0];
    },
    [entityIds, parentEntity]
  );

  const isCustomListsTable = tableName === 'customLists';
  const isNewDesign = tableName === 'BrowseOpus';
  const isNarrowScreen = tableName === 'TopLists';

  // Versions
  const isLink = Boolean(parentEntityId);
  const ParentEntityComponent = isLink
    ? ParentEntityItemLink
    : ParentEntityItem;

  return (
    <GenericComponentWrapper
      browseRelated={browseRelated}
      browseOpus={browseOpus}
      isTopLists={isTopLists}
    >
      <GenericComponent
        title={genericTitle}
        glossarySlug={glossarySlug}
        loading={loading}
        success={success}
        error={error}
        allowedFilters={allowedFilters}
        showToolbarDropdown={showToolbarDropdown}
        showToolbarOptions={showToolbarOptions}
        extraToolbarItems={extraToolbarItems}
        dataCy={dataCy}
        data-pw={dataCy}
        browseRelated={browseRelated}
        isNewDesign={isNewDesign}
        isNarrowScreen={isNarrowScreen}
        isSmallScreen={!isLargeScreen}
        customizedMode={customizedMode}
      >
        {parentEntity && isLargeScreen && (
          <>
            <VersionsLabel>
              This {parentEntityId ? 'version is part of' : entityType}
            </VersionsLabel>
            {parentEntity ? (
              <ParentEntityComponent
                entityId={parentEntity.id}
                entityType={parentEntity.type}
                displayName={parentEntity.displayName}
                primaryArtist={parentEntity.primaryArtist}
                thumbnailUrl={parentEntity.thumbnailUrl}
                releaseDate={parentEntity.releaseDate}
                isGroup={parentEntity.is_group}
                groupChildCount={parentEntity.group_child_count}
                entityData={entityData.parent.data}
                releaseCount={parentEntity.grouped_album_count}
                artistCount={parentEntity.artist_count}
                trackCount={parentEntity.grouped_track_count}
                preserveSearch
                isLink={isLink}
                isHighlighted={!isLink}
                isrc_list={parentEntity.isrc_list}
              />
            ) : (
              <ParentEntityItemLoader />
            )}
          </>
        )}
        <BrowseControls
          handleSearch={handleSearch}
          isSearchHidden={(!!parentEntity && isLargeScreen) || isSearchHidden}
          value={inputValue}
          browseEntityType={entityType}
          isLargeScreen={isLargeScreen}
          isExternalUser={isExternalUser}
          isExternalOneLabelUser={isExternalOneLabelUser}
          salesType={salesType}
          saveComponentSettings={saveComponentSettings}
          sortOrder={sortOrder}
          handleToggle={handleToggle}
          handleDropdown={changeSortMetric}
          dropdownItems={dropdownItems}
          activeItem={activeItem}
          tableName={tableName}
          globalFilters={globalFilters}
          browseRelated={browseRelated}
          isTabControlsHidden={isTabControlsHidden}
          isSalesTypeMobile={isTopLists}
          isReleaseChecked={isReleaseChecked}
          setReleaseChecked={setReleaseChecked}
          isRoundedDropdown={
            !isLargeScreen ? (isTopLists ? 'right' : 'all') : 'right'
          }
        />
        {parentEntity && !isLargeScreen && (
          <>
            <VersionsLabel>
              This {parentEntityId ? 'version is part of' : entityType}
            </VersionsLabel>
            {parentEntity ? (
              <ParentEntityComponent
                entityId={parentEntity.id}
                entityType={parentEntity.type}
                displayName={parentEntity.displayName}
                primaryArtist={parentEntity.primaryArtist}
                thumbnailUrl={parentEntity.thumbnailUrl}
                releaseDate={parentEntity.releaseDate}
                isGroup={parentEntity.is_group}
                groupChildCount={parentEntity.group_child_count}
                entityData={entityData.parent.data}
                releaseCount={parentEntity.grouped_album_count}
                artistCount={parentEntity.artist_count}
                trackCount={parentEntity.grouped_track_count}
                preserveSearch
                isLink={isLink}
                isHighlighted={!isLink}
                isrc_list={parentEntity.isrc_list}
              />
            ) : (
              <ParentEntityItemLoader />
            )}
          </>
        )}
        <ErrorBoundary error={reason} errorInfo={reason ? reason.cause : null}>
          <AnimatedLoadingWrapper
            loading={loading}
            browseRelated={browseRelated}
          >
            {isTopLists && <ActualDate>{actualTWTDate}</ActualDate>}
            <Table isBrowseTable entity={entityType}>
              {isLargeScreen &&
                (entityType === 'label' ? (
                  <SortingArrowTableHead
                    handleClick={changeSortMetric}
                    columnHeaders={tableHeaders}
                    activeHeader={sortMetric.value}
                    order={sortOrder}
                  />
                ) : (
                  <DropdownTableHead
                    sortMetric={sortMetric}
                    sortOrder={sortOrder}
                    setSortMetric={setSortMetric}
                    setSortOrder={setSortOrder}
                    changeSortMetric={changeSortMetric}
                    headers={tableHeaders}
                    withChangeMetric={withChangeMetric}
                    tableName={isTopLists ? 'TopLists' : 'BrowseOpus'}
                    redesignedTable={true}
                  />
                ))}
              <TableBody data-gtag="browse-opus-list">
                {entities && entities.length > 0
                  ? entities.map((entity: IInnerEntity): JSX.Element => {
                      const key = isCustomListsTable
                        ? entity.entity.id
                        : entityType === 'release' ||
                          tableName === 'Browse Opus Release'
                        ? entity?.album?.id
                        : entityType === 'version'
                        ? entity['track']?.id
                        : entity[entityType].id;
                      const entityTypeString = isCustomListsTable
                        ? entity.entity.type
                        : entityType === 'version' &&
                          tableName === 'Browse Opus Track'
                        ? 'track'
                        : entityType === 'version' &&
                          tableName === 'Browse Opus Release'
                        ? 'release'
                        : (entityType as EntityTypes);
                      const isHighlighted = checkHighlighted(entity);
                      return isLargeScreen ? (
                        <TableRow
                          key={key}
                          entity={entity}
                          browseEntityType={entityTypeString}
                          sortBy={sortMetric}
                          isLargeScreen={isLargeScreen}
                          tableHeaders={tableHeaders}
                          tableName={tableName}
                          newDesign={browseRelated || newDesign}
                          isHighlighted={isHighlighted}
                          releaseCompilation={releaseCompilation}
                          isTrackCountDisplayed={
                            salesType?.value !== 'physical' &&
                            entity?.trackCount &&
                            !!entity?.trackCount
                          }
                        />
                      ) : (
                        <BrowseTableRow
                          key={key}
                          entity={entity}
                          browseEntityType={entityTypeString}
                          tableHeaders={tableHeaders}
                          tableName={tableName}
                          sortBy={sortMetric}
                          isHighlighted={isHighlighted}
                          releaseCompilation={releaseCompilation}
                        />
                      );
                    })
                  : loading && <TableRowLoader />}
              </TableBody>
            </Table>

            {entities &&
              entities.length === 0 &&
              (isCustomListsTable ? (
                <BaseNoData>
                  <p>
                    Unfortunately there is no streams data available for the
                    items of the current custom list.
                  </p>
                </BaseNoData>
              ) : (
                <NoResults
                  browseEntityType={entityType as EntityTypes}
                  isLargeScreen={isLargeScreen}
                  searchValue={searchValue}
                  selectedSalesType={salesType}
                />
              ))}

            {entities && entities.length > 0 && (
              <Pagination
                current={entities.length}
                total={entityCount}
                offset={offset}
                hasMoreResults={entities.length < entityCount}
                onClick={setOffset}
                isWideButtonWithTotal
              />
            )}
          </AnimatedLoadingWrapper>

          {showDataWarning && (
            <DataWarning>
              Please note: by default, this Browse table is now displaying data
              in <strong>Album Equivalents</strong>.
            </DataWarning>
          )}
        </ErrorBoundary>
      </GenericComponent>
    </GenericComponentWrapper>
  );
};

export default BrowseTable;
